import { useState } from 'react';

import { EsocialEventType, type EsocialEventResponse, type EsocialFetchResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  useNotifications,
  type Column,
} from '../../components';
import { formatCnpj, formatDate, formatDateTime, formatInteger } from '../../format';
import {
  useCompanies,
  useCompanyContracts,
  useConfirmEsocialFetch,
  useEsocialEvents,
  useEsocialFetches,
  useFetchEsocialEvents,
} from '../queries';
import {
  ESOCIAL_EVENTS_COPY,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_TONES,
  EVENT_TYPE_LABELS,
  FETCH_STATUS_LABELS,
  FETCH_STATUS_TONES,
  confirmFailureMessage,
  fetchFailureMessage,
} from './esocialEvents';

/**
 * What the government already has on file, and the button that goes and gets it.
 *
 * Its own screen rather than a panel on `/equipe/conexao-esocial`, and the two
 * addresses say why: that one answers "can we reach the government right now?",
 * asked on a bad morning; this one answers "what has the government got on this
 * customer?", worked through on an ordinary one.
 *
 * Four decisions shape it.
 *
 * **It says what it does not do, first and in a warning tone.** An analyst who
 * believed a busca had filed movements would stop looking at the fila de
 * análise, which is the one place movements are actually decided. The sentence
 * is above the button rather than beside the results, because the belief forms
 * before the results arrive.
 *
 * **The company is required, unlike the diagnostic's.** A fetch is a query about
 * one employer's CNPJ; there is no useful case without one, and the button stays
 * disabled until one is chosen rather than offering a request that would be
 * refused.
 *
 * **A refused fetch is still a successful request.** eSocial saying no is an
 * answer, and it comes back as a run with `refused_consultations` and the
 * government's own wording — shown as a result, not as an error banner. The
 * banner is kept for the request itself failing: no client configured, a company
 * with no CNPJ, no certificate installed, a window the worker consultation would
 * not serve. Those mean nothing was asked.
 *
 * **A run is a slice of a company, and the screen says so.** Discovery is one
 * consultation per worker against a daily allowance of about ten calls, so the
 * result and the history both carry how many workers were consulted, how many
 * are left and what the run spent — which is the only way to tell "o eSocial
 * recusou", "a empresa não declarou nada" and "acabaram as chamadas" apart.
 *
 * **The history is a panel of its own and is always shown.** It is the whole
 * debugging argument for keeping a fetch as a row: an empty list of events means
 * *the employer declared nothing*, *the government refused us* or *nobody has
 * run this*, and only the history tells the three apart.
 *
 * **Confirming is a second panel and a second button**, never an option on the
 * first. A fetch is safe and a confirm creates movimentações, so the two are
 * different decisions taken at different moments -- and the confirm panel names
 * the busca it is about, because by the time somebody decides to commit one,
 * several may be sitting in the history. Only the runs that staged something and
 * have not been confirmed are offered; the rest would be a button that could
 * only produce a `409`.
 *
 * **The contract is required and cannot be derived.** eSocial answers about an
 * employer's CNPJ; which of that employer's contracts a person belongs to is not
 * in the government's answer. So it is a select, and the hint on it says why
 * rather than leaving somebody to wonder what they are choosing.
 */
export function EsocialEventsPage() {
  const [companyId, setCompanyId] = useState('');
  const [eventType, setEventType] = useState<EsocialEventType | ''>('');

  const companies = useCompanies();
  const selected = companyId === '' ? null : companyId;
  const events = useEsocialEvents(selected, eventType === '' ? null : eventType);
  const fetches = useEsocialFetches(selected);
  const run = useFetchEsocialEvents();
  const { notify } = useNotifications();

  const specific = fetchFailureMessage(run.error);

  return (
    <div className="page">
      <PageHeader title={ESOCIAL_EVENTS_COPY.title} description={ESOCIAL_EVENTS_COPY.description} />

      <Panel
        title={ESOCIAL_EVENTS_COPY.fetchTitle}
        description={ESOCIAL_EVENTS_COPY.fetchDescription}
      >
        <p className="panel__footnote panel__footnote--warning">
          {ESOCIAL_EVENTS_COPY.nothingIsApplied}
        </p>

        <SelectField
          label={ESOCIAL_EVENTS_COPY.company}
          value={companyId}
          onChange={setCompanyId}
          options={(companies.data?.items ?? []).map((company) => ({
            value: company.id,
            label: company.name,
          }))}
          placeholder={
            companies.isPending
              ? ESOCIAL_EVENTS_COPY.companiesLoading
              : ESOCIAL_EVENTS_COPY.companyNone
          }
          hint={ESOCIAL_EVENTS_COPY.companyHint}
        />

        <div className="form__actions">
          <button
            type="button"
            className="button"
            disabled={selected === null || run.isPending}
            onClick={() => {
              if (selected === null) {
                return;
              }
              run.mutate(
                { companyId: selected },
                {
                  onSuccess: (result) =>
                    notify(
                      result.fetch.failed +
                        result.fetch.refused_consultations +
                        (result.fetch.workers_remaining ?? 0) >
                        0
                        ? 'warning'
                        : 'success',
                      `${formatInteger(result.fetch.fetched)} evento(s) encontrado(s): ` +
                        `${formatInteger(result.fetch.staged)} guardado(s), ` +
                        `${formatInteger(result.fetch.skipped)} já tínhamos, ` +
                        `${formatInteger(result.fetch.failed)} não vieram.`,
                    ),
                },
              );
            }}
          >
            {run.isPending ? ESOCIAL_EVENTS_COPY.fetching : ESOCIAL_EVENTS_COPY.fetch}
          </button>
        </div>

        {run.isError && (
          <>
            {/* Our sentence when we have a better one, and the server's
                underneath rather than instead of it: theirs carries the detail
                ours cannot -- which company, which certificate, which setting. */}
            <FormError error={run.error} {...(specific !== undefined && { message: specific })} />
            {specific !== undefined && (
              <p className="panel__footnote">{ESOCIAL_EVENTS_COPY.fetchFailed}</p>
            )}
          </>
        )}

        {run.data !== undefined && <RunSummary fetch={run.data.fetch} />}
      </Panel>

      <ConfirmPanel companyId={selected} fetches={fetches.data?.items ?? []} />

      <Panel
        title={ESOCIAL_EVENTS_COPY.eventsTitle}
        description={ESOCIAL_EVENTS_COPY.eventsDescription}
      >
        <SelectField
          label={ESOCIAL_EVENTS_COPY.type}
          value={eventType}
          onChange={(value) => setEventType(value as EsocialEventType | '')}
          options={Object.values(EsocialEventType).map((value) => ({
            value,
            label: EVENT_TYPE_LABELS[value],
          }))}
          placeholder={ESOCIAL_EVENTS_COPY.allTypes}
        />

        <AsyncSection
          state={events}
          loadingLabel={ESOCIAL_EVENTS_COPY.eventsLoading}
          errorTitle={ESOCIAL_EVENTS_COPY.eventsFailed}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={ESOCIAL_EVENTS_COPY.eventsEmpty}
          emptyDescription={ESOCIAL_EVENTS_COPY.eventsEmptyDescription}
        >
          {(data) => (
            <DataTable
              id="esocial-events"
              caption={ESOCIAL_EVENTS_COPY.eventsCaption}
              columns={EVENT_COLUMNS}
              rows={data.items}
              rowKey={(row) => row.id}
              emptyTitle={ESOCIAL_EVENTS_COPY.eventsEmpty}
            />
          )}
        </AsyncSection>
      </Panel>

      <Panel
        title={ESOCIAL_EVENTS_COPY.historyTitle}
        description={ESOCIAL_EVENTS_COPY.historyDescription}
      >
        <AsyncSection
          state={fetches}
          loadingLabel={ESOCIAL_EVENTS_COPY.historyLoading}
          errorTitle={ESOCIAL_EVENTS_COPY.historyFailed}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={ESOCIAL_EVENTS_COPY.historyEmpty}
          emptyDescription={ESOCIAL_EVENTS_COPY.historyEmptyDescription}
        >
          {(data) => (
            <DataTable
              id="esocial-event-history"
              caption={ESOCIAL_EVENTS_COPY.historyCaption}
              columns={FETCH_COLUMNS}
              rows={data.items}
              rowKey={(row) => row.id}
              emptyTitle={ESOCIAL_EVENTS_COPY.historyEmpty}
            />
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/**
 * The second step: pick an unconfirmed run and a contract, and commit the batch.
 *
 * Three decisions shape this panel.
 *
 * **It only offers runs it can actually commit.** A run that staged nothing, or
 * one already confirmed, is not in the select -- a button whose only possible
 * outcome is a `409` teaches people to ignore error banners. When there is
 * nothing to offer, the panel says so instead of showing an empty select.
 *
 * **The contract is a required choice, and the hint says why.** eSocial answers
 * about an employer's CNPJ and does not say which of that employer's contracts a
 * person belongs to, so nothing here can derive it.
 *
 * **The result is a sentence with all three numbers in it.** "Confirmado" alone
 * would hide the event that was refused, which is the one thing on this screen
 * somebody has to go and look at.
 */
function ConfirmPanel({
  companyId,
  fetches,
}: {
  companyId: string | null;
  fetches: readonly EsocialFetchResponse[];
}) {
  const [fetchId, setFetchId] = useState('');
  const [contractId, setContractId] = useState('');
  const contracts = useCompanyContracts(companyId);
  const confirm = useConfirmEsocialFetch();
  const { notify } = useNotifications();

  const pending = fetches.filter((fetch) => fetch.is_confirmed !== true && fetch.staged > 0);
  const specific = confirmFailureMessage(confirm.error);
  const ready = fetchId !== '' && contractId !== '';

  return (
    <Panel
      title={ESOCIAL_EVENTS_COPY.confirmTitle}
      description={ESOCIAL_EVENTS_COPY.confirmDescription}
    >
      <p className="panel__footnote panel__footnote--warning">
        {ESOCIAL_EVENTS_COPY.confirmChangesNothingYet}
      </p>

      {companyId === null ? (
        <p className="panel__footnote">{ESOCIAL_EVENTS_COPY.noCompany}</p>
      ) : pending.length === 0 ? (
        <p className="panel__footnote">{ESOCIAL_EVENTS_COPY.confirmNothingToDo}</p>
      ) : (
        <>
          <SelectField
            label={ESOCIAL_EVENTS_COPY.confirmFetch}
            value={fetchId}
            onChange={setFetchId}
            options={pending.map((fetch) => ({
              value: fetch.id,
              label:
                `${formatDateTime(fetch.started_at)} · ` +
                `${formatInteger(fetch.staged)} evento(s) · ` +
                `${formatDate(fetch.window_start)} — ${formatDate(fetch.window_end)}`,
            }))}
            placeholder={ESOCIAL_EVENTS_COPY.confirmFetchNone}
            hint={ESOCIAL_EVENTS_COPY.confirmFetchHint}
          />

          <SelectField
            label={ESOCIAL_EVENTS_COPY.confirmContract}
            value={contractId}
            onChange={setContractId}
            options={(contracts.data?.items ?? []).map((contract) => ({
              value: contract.id,
              label: `${contract.insurer.name} · ${contract.subestipulante}/${contract.modulo}`,
            }))}
            placeholder={
              contracts.isPending
                ? ESOCIAL_EVENTS_COPY.confirmContractsLoading
                : ESOCIAL_EVENTS_COPY.confirmContractNone
            }
            hint={ESOCIAL_EVENTS_COPY.confirmContractHint}
          />

          <div className="form__actions">
            <button
              type="button"
              className="button"
              disabled={!ready || confirm.isPending}
              onClick={() => {
                if (!ready) {
                  return;
                }
                confirm.mutate(
                  { fetchId, contractId },
                  {
                    onSuccess: (result) => {
                      setFetchId('');
                      notify(
                        (result.fetch.refused ?? 0) > 0 ? 'warning' : 'success',
                        `${formatInteger(result.fetch.committed ?? 0)} movimentação(ões) na ` +
                          `fila, ${formatInteger(result.fetch.no_action ?? 0)} sem ação, ` +
                          `${formatInteger(result.fetch.refused ?? 0)} recusada(s).`,
                      );
                    },
                  },
                );
              }}
            >
              {confirm.isPending ? ESOCIAL_EVENTS_COPY.confirming : ESOCIAL_EVENTS_COPY.confirm}
            </button>
          </div>
        </>
      )}

      {confirm.isError && (
        <>
          <FormError error={confirm.error} {...(specific !== undefined && { message: specific })} />
          {specific !== undefined && (
            <p className="panel__footnote">{ESOCIAL_EVENTS_COPY.confirmFailed}</p>
          )}
        </>
      )}

      {confirm.data !== undefined && <CommitSummary fetch={confirm.data.fetch} />}
    </Panel>
  );
}

/** The three outcomes of the confirm that just ran, and who it was. */
function CommitSummary({ fetch }: { fetch: EsocialFetchResponse }) {
  return (
    <dl className="definitions" role="status">
      <div>
        <dt>{ESOCIAL_EVENTS_COPY.committed}</dt>
        <dd>{formatInteger(fetch.committed ?? 0)}</dd>
      </div>
      <div>
        <dt>{ESOCIAL_EVENTS_COPY.noAction}</dt>
        <dd>{formatInteger(fetch.no_action ?? 0)}</dd>
      </div>
      <div>
        <dt>{ESOCIAL_EVENTS_COPY.refusedEvents}</dt>
        <dd>{formatInteger(fetch.refused ?? 0)}</dd>
      </div>
      <div>
        <dt>{ESOCIAL_EVENTS_COPY.confirmedBy}</dt>
        <dd>{fetch.confirmed_by_display_name ?? <Dash />}</dd>
      </div>
      <div>
        <dt>{ESOCIAL_EVENTS_COPY.confirmedAt}</dt>
        <dd>{fetch.confirmed_at == null ? <Dash /> : formatDateTime(fetch.confirmed_at)}</dd>
      </div>
    </dl>
  );
}

/**
 * The four numbers of the run that just finished, plus whatever eSocial said.
 *
 * Shown even when nothing was found, because "found nothing" is the answer on a
 * quiet month and a screen that showed only the interesting runs would leave an
 * operator unsure whether the button had done anything at all.
 */
function RunSummary({ fetch }: { fetch: EsocialFetchResponse }) {
  return (
    <>
      <p className="panel__footnote" role="status">
        <span className={`badge badge--${FETCH_STATUS_TONES[fetch.status]}`}>
          {FETCH_STATUS_LABELS[fetch.status]}
        </span>
      </p>
      <dl className="definitions">
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.fetched}</dt>
          <dd>{formatInteger(fetch.fetched)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.staged}</dt>
          <dd>{formatInteger(fetch.staged)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.skipped}</dt>
          <dd>{formatInteger(fetch.skipped)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.failed}</dt>
          <dd>{formatInteger(fetch.failed)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.refusedTypes}</dt>
          <dd>{formatInteger(fetch.refused_consultations)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.workers}</dt>
          <dd>{formatInteger(fetch.workers_consulted ?? 0)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.workersRemaining}</dt>
          <dd>{formatInteger(fetch.workers_remaining ?? 0)}</dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.calls}</dt>
          <dd>
            {formatInteger(fetch.calls_spent)} / {formatInteger(fetch.call_budget)}
          </dd>
        </div>
        <div>
          <dt>{ESOCIAL_EVENTS_COPY.window}</dt>
          <dd>
            {formatDate(fetch.window_start)} — {formatDate(fetch.window_end)}
          </dd>
        </div>
      </dl>
      {/* The three sentences that tell the three empty results apart. Each is
          only shown when it is the true one, because a screen that printed all
          three every time would be a screen nobody reads. */}
      {fetch.budget_exhausted === true && (
        <p className="panel__footnote panel__footnote--warning">
          {ESOCIAL_EVENTS_COPY.budgetExhausted}
        </p>
      )}
      {fetch.roster_fallback === true && (
        <p className="panel__footnote panel__footnote--warning">
          {ESOCIAL_EVENTS_COPY.rosterFallback}
        </p>
      )}
      {fetch.fetched === 0 &&
        fetch.refused_consultations === 0 &&
        fetch.budget_exhausted !== true && (
          <p className="panel__footnote">{ESOCIAL_EVENTS_COPY.nothingFiled}</p>
        )}
      {fetch.failure_reason != null && (
        <p className="panel__footnote panel__footnote--warning">
          {/* eSocial's own code and wording, untranslated. It is already pt-BR,
              it is the government's rather than ours, and it is what an operator
              quotes when they open a ticket. */}
          {ESOCIAL_EVENTS_COPY.esocialResponse}
          {fetch.esocial_response_code != null && ` ${fetch.esocial_response_code}`} —{' '}
          {fetch.failure_reason}
        </p>
      )}
    </>
  );
}

/**
 * One staged event.
 *
 * The person is a masked CPF and the name eSocial holds, which is what the API
 * serves: the full registry number is encrypted at rest and there is no field in
 * the contract that could carry it here.
 */
const EVENT_COLUMNS: readonly Column<EsocialEventResponse>[] = [
  {
    key: 'type',
    header: ESOCIAL_EVENTS_COPY.type,
    render: (row) => EVENT_TYPE_LABELS[row.event_type],
  },
  {
    key: 'date',
    header: ESOCIAL_EVENTS_COPY.eventDate,
    render: (row) =>
      row.event_date == null ? (
        <Dash />
      ) : (
        <>
          {formatDate(row.event_date)}
          {row.event_end_date != null && ` — ${formatDate(row.event_end_date)}`}
        </>
      ),
  },
  {
    key: 'person',
    header: ESOCIAL_EVENTS_COPY.person,
    render: (row) =>
      row.worker_cpf_masked == null && row.worker_name == null ? (
        <Dash />
      ) : (
        <>
          <p>{row.worker_name ?? <Dash />}</p>
          <p className="table__muted">{row.worker_cpf_masked ?? '—'}</p>
        </>
      ),
  },
  {
    key: 'identifier',
    header: ESOCIAL_EVENTS_COPY.identifier,
    render: (row) => <code>{row.esocial_event_id}</code>,
  },
  {
    key: 'outcome',
    header: ESOCIAL_EVENTS_COPY.outcome,
    // The badge and the reason together, because the two answer different
    // questions: the badge says whether there is anything to do, and the reason
    // says what. A refusal with no reason beside it is a row nobody can act on.
    render: (row) => (
      <>
        <span className={`badge badge--${EVENT_STATUS_TONES[row.status]}`}>
          {EVENT_STATUS_LABELS[row.status]}
        </span>
        {row.outcome_reason != null && <p className="table__muted">{row.outcome_reason}</p>}
      </>
    ),
  },
  {
    key: 'staged',
    header: ESOCIAL_EVENTS_COPY.stagedAt,
    render: (row) => formatDateTime(row.staged_at),
  },
];

/** One run: who, when, over which window, and what came of it. */
const FETCH_COLUMNS: readonly Column<EsocialFetchResponse>[] = [
  {
    key: 'when',
    header: ESOCIAL_EVENTS_COPY.when,
    render: (row) => formatDateTime(row.started_at),
  },
  {
    key: 'who',
    header: ESOCIAL_EVENTS_COPY.who,
    render: (row) => row.requested_by_display_name,
  },
  {
    key: 'employer',
    header: 'CNPJ',
    render: (row) => <code>{formatCnpj(row.employer_cnpj)}</code>,
  },
  {
    key: 'window',
    header: ESOCIAL_EVENTS_COPY.window,
    render: (row) => `${formatDate(row.window_start)} — ${formatDate(row.window_end)}`,
  },
  {
    key: 'status',
    header: ESOCIAL_EVENTS_COPY.status,
    render: (row) => (
      <span className={`badge badge--${FETCH_STATUS_TONES[row.status]}`}>
        {FETCH_STATUS_LABELS[row.status]}
      </span>
    ),
  },
  {
    key: 'numbers',
    header: ESOCIAL_EVENTS_COPY.numbers,
    render: (row) =>
      `${formatInteger(row.fetched)} / ${formatInteger(row.staged)} / ` +
      `${formatInteger(row.skipped)} / ${formatInteger(row.failed)}`,
  },
  {
    key: 'coverage',
    header: ESOCIAL_EVENTS_COPY.coverage,
    // How much of the employer this run actually looked at, which is what tells
    // "recusada", "não declarou nada" and "acabaram as chamadas" apart in a
    // history where all three used to read as four zeros.
    render: (row) => (
      <>
        <p>
          {formatInteger(row.workers_consulted ?? 0)} / {formatInteger(row.calls_spent)}
          {' · '}
          {formatInteger(row.call_budget)}
        </p>
        {(row.workers_remaining ?? 0) > 0 && (
          <p className="table__muted">
            {ESOCIAL_EVENTS_COPY.workersRemaining}: {formatInteger(row.workers_remaining ?? 0)}
          </p>
        )}
      </>
    ),
  },
  {
    key: 'confirmed',
    header: ESOCIAL_EVENTS_COPY.outcome,
    // Not a badge: a run that has not been confirmed is not in a bad state, it
    // is simply a decision nobody has taken yet.
    render: (row) =>
      row.is_confirmed === true ? (
        `${formatInteger(row.committed ?? 0)} / ${formatInteger(row.no_action ?? 0)} / ` +
        `${formatInteger(row.refused ?? 0)}`
      ) : (
        <Dash />
      ),
  },
  {
    key: 'reason',
    header: ESOCIAL_EVENTS_COPY.reason,
    render: (row) => row.failure_reason ?? <Dash />,
  },
];

function Dash() {
  return <span className="table__muted">—</span>;
}
